import React, { useMemo } from 'react';
import type { MutableRefObject } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import type { Ad } from 'src/types/__generated__/graphql';
import { styledComponent } from 'src/utils/styled';
import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import PropertyCard from '../../PropertyCard';
import { defaultAdHref } from '../../PropertyCardLink';
import Loader from 'src/components/ui/Loader';
import Footer from 'src/components/other/Footer';
import { useSsr } from 'src/hooks/useSsr';
import { useManagedRouter } from 'src/hooks/useAppRouter';
import { AnchorPagination } from 'src/components/ui/AnchorPagination';

const ListContainer = styledComponent(Box)`
    background: ${colours.primary.O04};
    padding: 0 2rem 1rem 2rem;
    @media (width < ${breakpoints.desktopLarge}px) {
        padding: 0 1rem 1rem 1rem;
    }
    @media (width < ${breakpoints.desktopSmall}px) {
        padding: 0 2rem 1rem 2rem;
    }
    @media (width < ${breakpoints.tabletPortrait}px) {
        padding: 0 1rem 1rem 1rem;
    }
`;

type Props = {
    ads?: Ad[];
    isLoading: boolean;
    handleMouseEnter: (ad: Ad) => void;
    handleMouseLeave: () => void;
    scrollRestorationQueryKey: string;
    scrollRestorationRef: (ad: Pick<Ad, 'id'>) => MutableRefObject<HTMLDivElement | null> | undefined;
    isHybridView: boolean;
    showPagination?: boolean;
};

export const AdList: React.FC<Props> = ({
    ads,
    isLoading,
    handleMouseEnter,
    handleMouseLeave,
    scrollRestorationQueryKey,
    scrollRestorationRef,
    isHybridView,
    showPagination,
}) => {
    const { isServer } = useSsr();

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <ListContainer display="block" flex="1">
                <Grid spacing={2} container>
                    {ads?.map((ad, index) => (
                        <Grid
                            key={ad.id}
                            data-target="ad-card"
                            data-target-id={`ad-card-${index}`}
                            xs={12}
                            sm={6}
                            md={4}
                            lg={isHybridView ? 12 : 4}
                            xl={isHybridView ? 6 : 3}
                            item
                        >
                            <PropertyCard
                                ref={scrollRestorationRef(ad)}
                                onMouseEnter={() => handleMouseEnter(ad)}
                                onMouseLeave={handleMouseLeave}
                                ad={ad as Ad}
                                buildAdHref={(a) => `${defaultAdHref(a)}?${scrollRestorationQueryKey}=true`}
                                showUser
                                showTags
                            />
                        </Grid>
                    ))}
                </Grid>
                {!isServer && isLoading && (
                    <Box height="3rem" width="100%" mt="1rem">
                        <Loader />
                    </Box>
                )}
                {showPagination && !isLoading && <AdListPagination />}
            </ListContainer>

            <Box mt="auto">
                <Footer variant="compact" />
            </Box>
        </Box>
    );
};

const AdListPagination: React.FC = () => {
    const { router } = useManagedRouter();

    const currentPage = useMemo(() => {
        const queryPage = router.query.page as string | undefined;

        if (!queryPage) {
            return 1;
        }

        const pageAsNumber = parseInt(queryPage, 10);
        if (Number.isNaN(pageAsNumber)) {
            return 1;
        }

        return pageAsNumber;
    }, [router.query]);

    const { previousPageHref, nextPageHref } = useMemo(() => {
        const query = { ...router.query };

        const previousPage = currentPage - 1;
        const nextPage = currentPage + 1;

        return {
            previousPageHref:
                previousPage > 0
                    ? `${router.pathname}?${new URLSearchParams({ ...query, page: previousPage.toString() })}`
                    : undefined,
            nextPageHref: `${router.pathname}?${new URLSearchParams({ ...query, page: nextPage.toString() })}`,
        };
    }, [currentPage, router]);

    return (
        <AnchorPagination currentPage={currentPage} previousPageHref={previousPageHref} nextPageHref={nextPageHref} />
    );
};
